import { createContext, useContext, useEffect, useReducer } from 'react'
import type { FC, ReactNode } from 'react'
import axios from 'axios'
import { useLocalStorage } from '.'

interface User {
  id: string
  name: string
  email?: string
  token?: string
  afiliadoId?: string
}

interface InitialState {
  user: User
  isLoggedIn: boolean
}
interface UserSessionProviderState extends InitialState {
  setUser: (user: User) => void
  resetUser: () => void
  isLoggedIn: false
}

export type Actions = { type: 'SET_USER'; payload: User } | { type: 'RESET_USER' }

export const initialState: any = {
  user: (null as unknown) as User,
}

const UserSessionContext = createContext<UserSessionProviderState | undefined>(initialState)

export const useUserSession = () => {
  const context = useContext(UserSessionContext)

  if (!context) {
    throw new Error('Expected to be wrapped in a UserSessionProvider')
  }

  return context
}

function reducer(state: UserSessionProviderState, action: Actions) {
  switch (action.type) {
    case 'RESET_USER':
      return initialState

    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      }

    default:
      throw new Error('No action specified')
  }
}

export const UserSessionProvider: FC<{
  children?: ReactNode
  storage?: (key: string, initialValue: string) => [string, (value: Function | string) => void]
  user?: User
  onSetUser?: (user: User) => void
}> = ({ children, user = {}, onSetUser, storage = useLocalStorage }) => {
  const [userSession, setUserSession] = storage(
    'user-session',
    JSON.stringify({
      ...initialState,
      user,
    }),
  )

  const [state, dispatch] = useReducer(reducer, JSON.parse(userSession))

  useEffect(() => {
    setUserSession(JSON.stringify(state))
  }, [state, setUserSession])

  const setUser = (payload: User) => {
    dispatch({ type: 'SET_USER', payload })
    if (typeof onSetUser === 'function') {
      onSetUser(payload)
    }
  }

  function resetUser() {
    dispatch({ type: 'RESET_USER' })
  }

  return (
    <UserSessionContext.Provider
      value={{
        ...state,
        setUser,
        resetUser,
        isLoggedIn: state.user?.id,
      }}
    >
      {children}
    </UserSessionContext.Provider>
  )
}
